<template>
  <div class="project-power-company">
    <b-row>
      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('powerCompany') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('powerCompany')"
          label-for="powerCompany"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
          :invalid-feedback="invalidFeedbackNA"
          :state="requirementsValidState('powerCompany')"
        >
          <multiselect
            :placeholder="$t('powerCompany')"
            v-model="project.powerCompany"
            id="powerCompany"
            :options="getPowerCompanies"
            :allow-empty="false"
            :show-labels="false"
            track-by="company"
            label="company"
          >
          </multiselect>
        </b-form-group>

        <legend>
          <strong>{{ $t('powerCompanyMasterData') }}</strong>
        </legend>
        <div class="form-grid-container-left">
          <div class="label">{{ $t('street') }}</div>
          <div v-if="project.powerCompany">
            <strong>{{ project.powerCompany.street }}</strong>
          </div>
          <div v-else>{{ $t('not_applicable') }}</div>

          <div class="label">{{ $t('zip') }}</div>
          <div v-if="project.powerCompany">
            <strong>{{ project.powerCompany.zip }}</strong>
          </div>
          <div v-else>{{ $t('not_applicable') }}</div>

          <div class="label">{{ $t('city') }}</div>
          <div v-if="project.powerCompany">
            <strong>{{ project.powerCompany.city }}</strong>
          </div>
          <div v-else>{{ $t('not_applicable') }}</div>

          <div class="label">{{ $t('cosinusPhi') }}</div>
          <div v-if="project.powerCompany">
            <strong>{{ project.powerCompany.cosinusPhi }}</strong>
          </div>
          <div v-else>{{ $t('not_applicable') }}</div>

          <div class="label">{{ $t('operatorOnSiteElectricMeterChange') }}</div>
          <div v-if="project.powerCompany">
            <strong>{{ $t(project.powerCompany.operatorOnSiteElectricMeterChange) }}</strong>
          </div>
          <div v-else>{{ $t('not_applicable') }}</div>

          <div class="label">{{ $t('website') }}</div>
          <div v-if="project.powerCompany && project.powerCompany.website">
            <a :href="project.powerCompany.website" target="_blank">{{
              project.powerCompany.website
            }}</a>
          </div>
          <div v-else>{{ $t('not_applicable') }}</div>

          <div class="label">{{ $t('description') }}</div>
          <div
            v-if="project.powerCompany && project.powerCompany.description"
            v-html="project.powerCompany.description"
          ></div>
          <div v-else>{{ $t('not_applicable') }}</div>
        </div>

        <legend>
          <strong>{{ $t('measurementPointOperator') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('measurementPointOperator')"
          label-for="measurementPointOperator"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <multiselect
            :placeholder="$t('powerCompany')"
            v-model="project.measurementPointOperator"
            id="measurementPointOperator"
            :options="getMeasurementPointOperators"
            :allow-empty="true"
            :show-labels="false"
            track-by="name"
            label="name"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.name }} - {{ option.msbNumber }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ option.name }} - {{ option.msbNumber }}
            </template>
          </multiselect>
        </b-form-group>

        <legend>
          <strong>{{ $t('documentAttachments') }}</strong>
        </legend>
        <AttachmentList
          v-if="project.powerCompany"
          :attachments="project.powerCompany.documentAttachments"
          attachmentType="documentAttachments"
          :deletable="false"
        ></AttachmentList>
      </b-col>

      <b-col lg="6" md="6" sm="12">
        <legend>
          <strong>{{ $t('powerCompanyCommunication') }}</strong>
        </legend>

        <b-form-group
          horizontal
          :label="$t('netRegistrationSentAt')"
          label-for="netRegistrationSentAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="netRegistrationSentAt"
            selectionMode="single"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2019:2025"
          >
          </Calendar>
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('powerCompanyProjectNumber')"
          label-for="powerCompanyProjectNumber"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.powerCompanyProjectNumber"
            type="text"
            :placeholder="$t('powerCompanyProjectNumber')"
          />
        </b-form-group>
        <hr />
        <b-form-group
          horizontal
          :label="$t('injectionConfirmationReceivedAt')"
          label-for="injectionConfirmationReceivedAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="injectionConfirmationReceivedAt"
            selectionMode="single"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2019:2025"
          >
          </Calendar>
        </b-form-group>

        <hr />
        <b-form-group
          horizontal
          :label="$t('operationRegistrationSentAt')"
          label-for="operationRegistrationSentAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="operationRegistrationSentAt"
            selectionMode="single"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2019:2025"
          >
          </Calendar>
        </b-form-group>

        <b-form-group
          horizontal
          :label="$t('powerCompanyProjectNumber2')"
          label-for="powerCompanyProjectNumber2"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.powerCompanyProjectNumber2"
            type="text"
            :placeholder="$t('powerCompanyProjectNumber2')"
          />
        </b-form-group>
        <hr />
        <b-form-group
          horizontal
          :label="$t('completionMessageSentAt')"
          label-for="completionMessageSentAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="completionMessageSentAt"
            selectionMode="single"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2019:2025"
          >
          </Calendar>
        </b-form-group>
        <b-form-group
          horizontal
          :label="$t('powerCompanyProjectNumber3')"
          label-for="powerCompanyProjectNumber3"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <b-form-input
            v-model="project.powerCompanyProjectNumber3"
            type="text"
            :placeholder="$t('powerCompanyProjectNumber3')"
          />
        </b-form-group>
        <hr />

        <b-form-group
          horizontal
          :label="$t('powerCompanyClearanceAt')"
          label-for="powerCompanyClearanceAt"
          label-class="text-md-right"
          :label-cols="4"
          breakpoint="md"
        >
          <Calendar
            v-model="powerCompanyClearanceAt"
            selectionMode="single"
            class="p-column-filter"
            dateFormat="dd.mm.yy"
            :placeholder="$t('date')"
            :showWeek="true"
            :yearNavigator="true"
            :monthNavigator="true"
            yearRange="2019:2025"
          >
          </Calendar>
        </b-form-group>
        <legend>
          <strong>{{ $t('notes') }}</strong>
        </legend>
        <b-form-group horizontal breakpoint="md">
          <ckeditor
            :editor="editor"
            v-model="project.powerCompanyDescription"
            :config="editorConfig"
          ></ckeditor>
        </b-form-group>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import Calendar from 'primevue/calendar';
import CKEditor from '@ckeditor/ckeditor5-vue2';
import InlineEditor from '@ckeditor/ckeditor5-build-inline';
import { mapGetters } from 'vuex';
import AttachmentList from '@/components/AttachmentList';

export default {
  components: {
    Multiselect,
    Calendar,
    ckeditor: CKEditor.component,
    AttachmentList,
  },
  props: {
    project: { type: Object },
  },
  data() {
    return {
      editor: InlineEditor,
      editorConfig: {},
    };
  },
  computed: {
    ...mapGetters(['getPowerCompanies', 'getMeasurementPointOperators']),
    netRegistrationSentAt: {
      get() {
        return this.$calendarFormat(this.project.netRegistrationSentAt);
      },
      set(value) {
        this.project.netRegistrationSentAt = value;
      },
    },
    injectionConfirmationReceivedAt: {
      get() {
        return this.$calendarFormat(this.project.injectionConfirmationReceivedAt);
      },
      set(value) {
        this.project.injectionConfirmationReceivedAt = value;
      },
    },
    operationRegistrationSentAt: {
      get() {
        return this.$calendarFormat(this.project.operationRegistrationSentAt);
      },
      set(value) {
        this.project.operationRegistrationSentAt = value;
      },
    },
    completionMessageSentAt: {
      get() {
        return this.$calendarFormat(this.project.completionMessageSentAt);
      },
      set(value) {
        this.project.completionMessageSentAt = value;
      },
    },
    powerCompanyClearanceAt: {
      get() {
        return this.$calendarFormat(this.project.powerCompanyClearanceAt);
      },
      set(value) {
        this.project.powerCompanyClearanceAt = value;
      },
    },
    invalidFeedbackNA() {
      return this.$t('preconditionNA');
    },
  },
  methods: {
    /**
     * check requirementsValidState to show mark input fields
     * @todo DRY
     */
    requirementsValidState(field) {
      if (!this.project.netRegistrationRequirements) return false;

      const requirement = this.project.netRegistrationRequirements.find(
        (requirement) => requirement.name === field
      );
      return requirement && requirement.fullfilledState === 'YES' ? true : false;
    },
  },
};
</script>

<style scoped lang="scss">
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container-left .label {
  text-align: right;
}
</style>
